import {
  PriorPolicyChallengeFormValuesType,
  PriorPolicyChallengeFieldsType,
} from 'QuoteApp/services/endpoints/PostChallengePriorPolicy';
import React, { useMemo } from 'react';
import { Column, FormInput } from 'Shared/components';
import {
  abbreviateNumber,
  validateCoverageLimitWithK,
  cleanCoverageLimitInput,
} from 'Shared/utils';
import {
  formatDateShortYearUTC,
  monthDateYearNormalizerShort,
  isServerDate,
  parseDateSlashFormatShort,
  formatDateMonthYear,
} from 'Shared/utils/date';
import styles from './styles.module.css';
import { FormikErrors } from 'formik';
import { generateFieldDataTestId } from 'QuoteApp/utils/formik';
import {
  PriorPolicyCoverageType,
  PriorPolicyResponseType,
} from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers/AppProvider';

// Coverage limit validation functions (now using shared utilities)

// Check if at least one coverage limit is provided (mirrors Yup limits validation)
const validateAtLeastOneCoverageLimit = (values: {
  biLimit?: string | null;
  cslLimit?: string | null;
  pipLimit?: string | null;
}): boolean => {
  const { biLimit, cslLimit, pipLimit } = values;
  return !!(biLimit?.trim() || cslLimit?.trim() || pipLimit?.trim());
};

// Helper to get all coverage values for validation
const getCoverageValues = (
  values: PriorPolicyChallengeFormValuesType,
  identifier: string
) => ({
  biLimit: values[identifier].biLimit,
  cslLimit: values[identifier].cslLimit,
  pipLimit: values[identifier].pipLimit,
});

// Combined validation function for coverage limits (DRY implementation)
const getCoverageErrorMessage = (
  fieldValue: string | null | undefined,
  type: 'BI' | 'CSL' | 'PIP',
  allCoverageValues: {
    biLimit?: string | null;
    cslLimit?: string | null;
    pipLimit?: string | null;
  },
  errors: FormikErrors<PriorPolicyChallengeFormValuesType>,
  identifier: string
): string | undefined => {
  const value = fieldValue?.toString() || '';

  // First check individual field validation using shared utility
  const validation = validateCoverageLimitWithK(value, type);
  if (!validation.isValid) {
    return validation.errorMessage;
  }

  // Then check if at least one coverage limit is provided
  const hasAtLeastOne = validateAtLeastOneCoverageLimit(allCoverageValues);
  const limitsError = errors[identifier]?.limits;

  if (!hasAtLeastOne && typeof limitsError === 'string') {
    return limitsError;
  }

  // Finally return any other field-specific errors
  const fieldName =
    type === 'BI' ? 'biLimit' : type === 'CSL' ? 'cslLimit' : 'pipLimit';
  return errors[identifier]?.[fieldName];
};

// Coverage limit change handlers
const handleCoverageChange = (
  fieldName: string,
  type: 'BI' | 'CSL' | 'PIP',
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  const allowSlash = type === 'BI';
  const cleanedValue = cleanCoverageLimitInput(rawValue, allowSlash);

  // Store the cleaned value
  setFieldValue(fieldName, cleanedValue, false);
};

// AM Best Number change handler - only allow numeric input (max 10 digits)
const handleAmBestNumberChange = (
  fieldName: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  // Only allow numeric characters and limit to 10 digits (max value 9999999999)
  const numericOnly = rawValue.replace(/[^0-9]/g, '');
  const limitedValue = numericOnly.slice(0, 10);

  setFieldValue(fieldName, limitedValue, false);
};

// Date validation function for MM/DD/YY format (2-digit year)
const isValidMMDDYYDate = (dateString: string): boolean => {
  if (!dateString || dateString.length < 8) return false;

  // Check if it matches MM/DD/YY format
  const mmddyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{2}$/;
  if (!mmddyyRegex.test(dateString)) return false;

  // Use our consistent date parsing utility instead of date-fns with non-deterministic reference date
  try {
    const parsedDate = parseDateSlashFormatShort(dateString);
    return parsedDate !== null;
  } catch {
    return false;
  }
};

// Helper function to get the display value for a date field (DRY implementation)
const getDateDisplayValue = (dateValue: string | null | undefined): string => {
  if (!dateValue) return '';

  return isServerDate(dateValue)
    ? parseISODateForDisplay(dateValue.toString())
    : dateValue.toString();
};

// Check if a date should show validation error (complete but invalid)
const shouldShowDateError = (dateString: string): boolean => {
  if (!dateString) return false;

  // Don't show error for ISO format dates (initial values)
  if (isServerDate(dateString)) return false;

  // Only show error if the date appears complete (8 characters) but is invalid MM/DD/YY format
  if (dateString.length >= 8) {
    return !isValidMMDDYYDate(dateString);
  }

  return false;
};

// Parse ISO date string to MM/DD/YY or MM/YY format for display
export const parseISODateForDisplay = (
  isoDateString: string | undefined | null
): string => {
  if (!isoDateString) return '';

  try {
    // Check if date is in YYYY-MM format (e.g., "2026-10")
    const yearMonthPattern = /^\d{4}-\d{2}$/;
    if (yearMonthPattern.test(isoDateString)) {
      // Parse YYYY-MM format and convert to MM/YY
      const [year, month] = isoDateString.split('-');
      const twoDigitYear = year.slice(-2); // Get last 2 digits of year
      return `${month}/${twoDigitYear}`;
    }

    // Handle ISO format dates like "2026-07-30T00:00:00.000+00:00"
    return formatDateShortYearUTC(isoDateString);
  } catch (error) {
    // If parsing fails, return the original value or empty string
    return isoDateString || '';
  }
};

// Date onChange handler with formatting and validation
const handleDateChange = (
  fieldName: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
) => (e: React.ChangeEvent<HTMLInputElement>) => {
  const rawValue = e.target.value;
  const formattedValue = monthDateYearNormalizerShort(rawValue);

  // Store the formatted value so user can see it
  setFieldValue(fieldName, formattedValue, false);
};

type PriorPolicyChallengeFormPropsType = {
  identifier: string;
  values: PriorPolicyChallengeFormValuesType;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  isEditing: boolean;
  errors: FormikErrors<PriorPolicyChallengeFormValuesType>;
  priorPolicy: PriorPolicyResponseType['attributes'] | null;
  isAdding?: boolean;
};
const getInputProps = (
  fieldName: keyof PriorPolicyChallengeFieldsType,
  values: PriorPolicyChallengeFormPropsType['values'],
  identifier: string,
  setFieldValue: PriorPolicyChallengeFormPropsType['setFieldValue'],
  errors: PriorPolicyChallengeFormPropsType['errors']
) => {
  const fullFieldName = `${identifier}.${fieldName}`;
  const fieldValue = values[identifier][fieldName];
  const fieldError = errors[identifier]?.[fieldName];
  return {
    name: fullFieldName,
    className: styles.formInput,
    'data-testid': generateFieldDataTestId(fieldName),
    variant: 'selectAutocomplete' as const,
    onChange: (value: string) => {
      setFieldValue(fullFieldName, value);
    },
    value: fieldValue?.toString() || '',
    errorMessage: typeof fieldError === 'string' ? fieldError : undefined,
  };
};

const PriorPolicyChallengeForm = ({
  identifier,
  values,
  setFieldValue,
  isEditing,
  errors,
  priorPolicy,
}: PriorPolicyChallengeFormPropsType) => {
  const { config } = useAppContext();

  const carriers = useMemo(() => {
    return config?.drivers.currentInsurance.carrierName;
  }, [config]);

  const states = useMemo(() => {
    return config?.drivers.driverLicense.state;
  }, [config]);

  const renderEdit = () => {
    return (
      <>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'carrierName',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            options={Object.entries(carriers || {}).reduce(
              (acc, [_key, value]) => {
                acc[value] = value;
                return acc;
              },
              {} as Record<string, string>
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'policyNumber',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`${identifier}.policyNumber`, e.target.value);
            }}
            variant="text"
            placeholder="Policy #"
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'startDate',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            className={styles.formInputDate}
            variant="text"
            placeholder="MM/DD/YY"
            value={getDateDisplayValue(values[identifier].startDate)}
            onChange={handleDateChange(
              `${identifier}.startDate`,
              setFieldValue
            )}
            errorMessage={
              shouldShowDateError(
                values[identifier].startDate?.toString() || ''
              )
                ? 'Invalid format'
                : errors[identifier]?.startDate
            }
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'endDate',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            className={styles.formInputDate}
            variant="text"
            placeholder="MM/DD/YY"
            value={getDateDisplayValue(values[identifier].endDate)}
            onChange={handleDateChange(`${identifier}.endDate`, setFieldValue)}
            errorMessage={
              shouldShowDateError(values[identifier].endDate?.toString() || '')
                ? 'Invalid format'
                : errors[identifier]?.endDate
            }
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'amBestNumber',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            variant="text"
            onChange={handleAmBestNumberChange(
              `${identifier}.amBestNumber`,
              setFieldValue
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            {...getInputProps(
              'state',
              values,
              identifier,
              setFieldValue,
              errors
            )}
            options={Object.entries(states || {}).reduce((acc, [key]) => {
              acc[key] = key;
              return acc;
            }, {} as Record<string, string>)}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            name={`${identifier}.biLimit`}
            className={styles.formInput}
            data-testid={generateFieldDataTestId('biLimit')}
            variant="text"
            placeholder="100K/300K"
            value={values[identifier].biLimit?.toString() || ''}
            onChange={handleCoverageChange(
              `${identifier}.biLimit`,
              'BI',
              setFieldValue
            )}
            errorMessage={getCoverageErrorMessage(
              values[identifier].biLimit,
              'BI',
              getCoverageValues(values, identifier),
              errors,
              identifier
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            name={`${identifier}.cslLimit`}
            className={styles.formInput}
            data-testid={generateFieldDataTestId('cslLimit')}
            variant="text"
            placeholder="100K"
            value={values[identifier].cslLimit?.toString() || ''}
            onChange={handleCoverageChange(
              `${identifier}.cslLimit`,
              'CSL',
              setFieldValue
            )}
            errorMessage={getCoverageErrorMessage(
              values[identifier].cslLimit,
              'CSL',
              getCoverageValues(values, identifier),
              errors,
              identifier
            )}
          />
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <FormInput
            name={`${identifier}.pipLimit`}
            className={styles.formInput}
            data-testid={generateFieldDataTestId('pipLimit')}
            variant="text"
            placeholder="10K"
            value={values[identifier].pipLimit?.toString() || ''}
            onChange={handleCoverageChange(
              `${identifier}.pipLimit`,
              'PIP',
              setFieldValue
            )}
            errorMessage={getCoverageErrorMessage(
              values[identifier].pipLimit,
              'PIP',
              getCoverageValues(values, identifier),
              errors,
              identifier
            )}
          />
        </Column>
      </>
    );
  };

  const getCoverage = (
    coverageType: PriorPolicyCoverageType['coverageType']
  ) => {
    return (
      priorPolicy?.coverages.find(
        coverage => coverage.coverageType === coverageType
      ) || {
        coverageType,
        limitPerPerson: 0,
        limitPerAccident: 0,
      }
    );
  };

  const renderCoverageLimits = (coverage: PriorPolicyCoverageType) => {
    // For BI (bodilyInjury), show both values with slash
    if (coverage.coverageType === 'bodilyInjury') {
      return (
        <>
          <div>
            {coverage?.limitPerPerson
              ? `$${abbreviateNumber(Number(coverage.limitPerPerson))}`
              : '-'}
            /
            {coverage.limitPerAccident
              ? `$${abbreviateNumber(Number(coverage.limitPerAccident))}`
              : '-'}
          </div>
        </>
      );
    }

    // For CSL and PIP, show only the single limitPerPerson value
    return (
      <>
        <div>
          {coverage?.limitPerPerson
            ? `$${abbreviateNumber(Number(coverage.limitPerPerson))}`
            : '-'}
        </div>
      </>
    );
  };

  const renderReadOnly = () => {
    const carrierName =
      carriers?.[values[identifier].carrierName] ||
      values[identifier].carrierName;

    return (
      <>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <div className={styles.priorPolicyChallengeReadOnlyAttributeText}>
            {carrierName || '-'}
          </div>
        </Column>
        <Column
          variant="eighth"
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <div className={styles.priorPolicyChallengeReadOnlyAttributeText}>
            {values[identifier].policyNumber || '-'}
          </div>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{formatDateMonthYear(values[identifier].startDate)}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{formatDateMonthYear(values[identifier].endDate)}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{values[identifier].amBestNumber || '-'}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          <span>{values[identifier].state || '-'}</span>
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          {renderCoverageLimits(getCoverage('bodilyInjury'))}
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          {renderCoverageLimits(getCoverage('combinedSingleLimit'))}
        </Column>
        <Column
          variant={'eighth'}
          className={styles.priorPolicyChallengeReadOnlyAttribute}
        >
          {renderCoverageLimits(getCoverage('personalInjuryProtection'))}
        </Column>
      </>
    );
  };

  return (
    <div
      data-testid={`prior-policy-challenge-read-only-attributes-${identifier}`}
      className={styles.priorPolicyChallengeReadOnlyAttributesWrapper}
    >
      {isEditing ? renderEdit() : renderReadOnly()}
    </div>
  );
};

export default PriorPolicyChallengeForm;
